import axios from "axios";
import { getApiBaseUrl } from "../config/api";

export interface HolidaySettings {
  country: string;
  state?: string;
  showHolidays: boolean;
  holidayColor: string;
}

export interface DisplaySettings {
  showTimezoneIndicator: boolean; // Show indicator when entry timezone differs from browser timezone
  showOverlapIndicator: boolean; // Show indicator when entries overlap within the same project
  showActiveEntryAnimations: boolean; // Show pulsing effects when a time entry is running
  showBudgetWarnings: boolean; // Highlight rows/entries that exceed project budget in reporting and dashboard
  overlapIndicatorColor: string; // Row/icon highlight color for overlap warnings (hex, default #FEF2F2)
  budgetWarningColor: string; // Row highlight color for budget violations (hex, default #FEF2F2)
  defaultCurrency: string; // ISO currency code (default: 'EUR')
}

export interface TimezoneSettings {
  preferredTimezone: string | null; // IANA timezone identifier, null means use browser default
}

export interface LocaleSettings {
  weekStartsOn: 0 | 1 | 'auto' | null; // 0 = Sunday, 1 = Monday, 'auto' = detect from locale, null = default to auto
}

export interface ProjectSettings {
  autoArchiveMonths: number; // 12 | 24 | 36
}

export interface Country {
  code: string;
  name: string;
}

export interface State {
  code: string;
  name: string;
}

class UserSettingsService {
  private baseURL: string;

  constructor() {
    // Use centralized API configuration
    this.baseURL = getApiBaseUrl();
  }

  private getAuthToken(): string {
    return localStorage.getItem("authToken") || "";
  }

  /**
   * Get current user's settings
   */
  async getUserSettings(): Promise<{
    holidaySettings: HolidaySettings;
    displaySettings: DisplaySettings;
    timezoneSettings: TimezoneSettings;
    localeSettings: LocaleSettings;
    projectSettings: ProjectSettings;
    /** @deprecated legacy field — read only for migration to displaySettings.defaultCurrency */
    currencySettings?: { defaultCurrency?: string };
  }> {
    try {
      const response = await axios.get(`${this.baseURL}/user-settings`, {
        headers: {
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch user settings:", error);
      throw new Error("Failed to fetch user settings");
    }
  }

  /**
   * Update user's holiday settings
   */
  async updateHolidaySettings(
    holidaySettings: Partial<HolidaySettings>
  ): Promise<{
    holidaySettings: HolidaySettings;
    displaySettings: DisplaySettings;
    timezoneSettings: TimezoneSettings;
    localeSettings: LocaleSettings;

    message: string;
  }> {
    try {
      const response = await axios.put(
        `${this.baseURL}/user-settings`,
        { holidaySettings },
        {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update holiday settings:", error);
      throw new Error("Failed to update holiday settings");
    }
  }

  /**
   * Update user's display settings
   */
  async updateDisplaySettings(
    displaySettings: Partial<DisplaySettings>
  ): Promise<{
    holidaySettings: HolidaySettings;
    displaySettings: DisplaySettings;
    timezoneSettings: TimezoneSettings;
    localeSettings: LocaleSettings;

    message: string;
  }> {
    try {
      const response = await axios.put(
        `${this.baseURL}/user-settings`,
        { displaySettings },
        {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update display settings:", error);
      throw new Error("Failed to update display settings");
    }
  }

  /**
   * Update user's timezone settings
   */
  async updateTimezoneSettings(
    timezoneSettings: Partial<TimezoneSettings>
  ): Promise<{
    holidaySettings: HolidaySettings;
    displaySettings: DisplaySettings;
    timezoneSettings: TimezoneSettings;
    localeSettings: LocaleSettings;

    message: string;
  }> {
    try {
      const response = await axios.put(
        `${this.baseURL}/user-settings`,
        { timezoneSettings },
        {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update timezone settings:", error);
      throw new Error("Failed to update timezone settings");
    }
  }

  /**
   * Update user's locale settings
   */
  async updateLocaleSettings(
    localeSettings: Partial<LocaleSettings>
  ): Promise<{
    holidaySettings: HolidaySettings;
    displaySettings: DisplaySettings;
    timezoneSettings: TimezoneSettings;
    localeSettings: LocaleSettings;

    message: string;
  }> {
    try {
      const response = await axios.put(
        `${this.baseURL}/user-settings`,
        { localeSettings },
        {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update locale settings:", error);
      throw new Error("Failed to update locale settings");
    }
  }

  /**
   * Update user's project settings
   */
  async updateProjectSettings(
    projectSettings: Partial<ProjectSettings>
  ): Promise<{
    projectSettings: ProjectSettings;
    message: string;
  }> {
    try {
      const response = await axios.put(
        `${this.baseURL}/user-settings`,
        { projectSettings },
        {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update project settings:", error);
      throw new Error("Failed to update project settings");
    }
  }

  /**
   * Get available countries
   */
  async getCountries(): Promise<{ countries: Country[] }> {
    try {
      const response = await axios.get(
        `${this.baseURL}/user-settings/countries`,
        {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch countries:", error);
      throw new Error("Failed to fetch countries");
    }
  }

  /**
   * Get available states for a country
   */
  async getStates(country: string): Promise<{ states: State[] }> {
    try {
      const response = await axios.get(
        `${this.baseURL}/user-settings/states/${country}`,
        {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch states:", error);
      throw new Error("Failed to fetch states");
    }
  }
}

export const userSettingsService = new UserSettingsService();
